/**
 * useMyBusinesses — React hook that fetches the current user's owned
 * / managed businesses via ``GET /api/businesses/mine`` and caches
 * them in local state. The header switcher chip and profile "My
 * businesses" section both consume this.
 *
 * The hook also owns the ``activeBusinessId`` — the business the user
 * is currently POSTING as. Stored in localStorage so it survives
 * navigation and page reloads. ``null`` means "post as myself
 * (Individual)".
 */
import { useCallback, useEffect, useState } from "react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const STORAGE_KEY = "sp:active-business";

export function useMyBusinesses() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeId, setActiveIdRaw] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || null; } catch { return null; }
  });

  const reload = useCallback(async () => {
    if (!user?.id) { setItems([]); return; }
    setLoading(true);
    try {
      const { data } = await http.get("/businesses/mine");
      setItems(data?.items || []);
    } catch (e) {
      // Silent — non-owners simply have no businesses.
      console.debug("useMyBusinesses reload skipped:", e?.response?.status);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { reload(); }, [reload]);

  const setActive = useCallback((id) => {
    setActiveIdRaw(id || null);
    try {
      if (id) localStorage.setItem(STORAGE_KEY, id);
      else localStorage.removeItem(STORAGE_KEY);
    } catch { /* noop */ }
  }, []);

  const active = items.find((b) => b.id === activeId) || null;
  return { items, active, activeId, setActive, loading, reload };
}
